import { z } from 'zod';
import { IsoDateTimeSchema, SegmentSchema, UuidSchema } from './common.js';

export const FunctionRoleSchema = z.enum([
  'projektleiter',
  'bereichsleiter',
  'kaufm_leitung',
  'mtb',
  'service_leitung',
  'manager',
  'kuse_admin',
]);
export type FunctionRole = z.infer<typeof FunctionRoleSchema>;

/**
 * Funktionsrollen, die zwingend an ein Kundensegment gebunden sind (`scope_segment`).
 * Single Source of Truth für: Rechte-Matrix (`permissions.ts`), Server-Validierung
 * (`routes/admin.ts`) und Admin-UI (`pages/admin.tsx`) — damit die drei nicht
 * auseinanderdriften.
 *
 * Die übrigen Rollen (`kaufm_leitung`, `projektleiter`) sind global bzw. ownership-
 * gescoped und MÜSSEN `scope_segment: null` haben.
 *
 * ACHTUNG (F2): `kuse_admin` ist ebenfalls segmentpflichtig, aber NICHT gleichbedeutend
 * mit „darf Segment-Notizen schreiben" — `canWriteSegmentNotes` (permissions.ts) führt dafür
 * bewusst eine eigene, engere Rollenliste statt sich auf diese Konstante zu verlassen.
 */
export const SEGMENT_SCOPED_ROLES = [
  'bereichsleiter',
  'manager',
  'mtb',
  'service_leitung',
  'kuse_admin',
] as const satisfies readonly FunctionRole[];

export function isSegmentScopedRole(role: FunctionRole): boolean {
  return (SEGMENT_SCOPED_ROLES as readonly FunctionRole[]).includes(role);
}

export const FUNCTION_ROLE_LABELS: Record<FunctionRole, string> = {
  projektleiter: 'Projektleiter',
  bereichsleiter: 'Bereichsleiter',
  kaufm_leitung: 'Kaufm. Leitung',
  mtb: 'MTB',
  service_leitung: 'Service-Leitung',
  manager: 'Manager',
  kuse_admin: 'KuSe-Admin',
};

export const UserFunctionRoleSchema = z.object({
  user_function_role_id: UuidSchema,
  user_id: UuidSchema,
  role: FunctionRoleSchema,
  scope_segment: SegmentSchema.nullable(),
  created_at: IsoDateTimeSchema,
});
export type UserFunctionRole = z.infer<typeof UserFunctionRoleSchema>;

export const SessionUserSchema = z.object({
  user_id: UuidSchema,
  email: z.string().email(),
  name: z.string(),
  is_admin: z.boolean(),
  is_test_user: z.boolean(),
  function_roles: z.array(
    z.object({
      role: FunctionRoleSchema,
      scope_segment: SegmentSchema.nullable(),
    }),
  ),
  mode: z.enum(['dev-stub', 'azure-ad']),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;
